<template>
  <div>
    <b-container fluid class="login-wrapper">
      <b-row no-gutters class="h-100">
        <b-col md="4" cols="12">
          <form class="login-form" @submit.prevent="saveNewPassword">
            <div class="d-flex flex-column justify-content-center h-100">
              <div class="mb-5 login-logo">
                <img :src="logoHomeImg" alt="Logo" />
                <h4 class="text-right">Rápido, seguro y digital</h4>
              </div>
              <label class="lm-input-label">
                {{ $t('password') }}
                <div class="position-relative">
                  <input
                    :type="showInputValue ? 'text' : 'password'"
                    name="password"
                    class="lm-input form-control"
                    :class="{ 'error': $v.form.newPassword.$error }"
                    v-model="form.newPassword"
                    @input="evaluatePassword"
                  >
                  <font-awesome-icon
                    id="font-awesome-icon-eye"
                    v-if="!showInputValue"
                    icon="eye"
                    size="sm"
                    class="clickable eyes"
                    @click="showInputValue = true"
                    />
                  <font-awesome-icon
                    id="font-awesome-icon-eye-slash"
                    v-if="showInputValue"
                    icon="eye-slash"
                    size="sm"
                    class="clickable eyes"
                    @click="showInputValue = false"
                    />
                </div>
                <small class="lm-input-caption" v-if="$v.form.newPassword.$error">Campo inválido</small>

                <p v-if="passwordStrength">
                  Fortaleza de la contraseña: {{ passwordScoreText }}
                </p>
                <p v-if="passwordStrength && passwordStrength.feedback.suggestions.length">
                  Sugerencias:
                  <ul>
                    <li v-for="suggestion in passwordStrength.feedback.suggestions" :key="suggestion">{{ suggestion }}</li>
                  </ul>
                </p>
              </label>
              <label class="lm-input-label">
                {{ $t('password_again') }}
                <div class="position-relative">
                  <input
                    :type="showInputValue ? 'text' : 'password'"
                    class="lm-input form-control"
                    :class="{ 'error': $v.form.newPasswordAgain.$error }"
                    v-model.lazy="$v.form.newPasswordAgain.$model"
                  >
                  <font-awesome-icon
                    id="font-awesome-icon-eye"
                    v-if="!showInputValue"
                    icon="eye"
                    size="sm"
                    class="clickable eyes"
                    @click="showInputValue = true"
                    />
                  <font-awesome-icon
                    id="font-awesome-icon-eye-slash"
                    v-if="showInputValue"
                    icon="eye-slash"
                    size="sm"
                    class="clickable eyes"
                    @click="showInputValue = false"
                    />
                </div>
                <small class="lm-input-caption" v-if="$v.form.newPasswordAgain.$error">Las contraseñas son diferentes</small>
              </label>
              <button type="submit" class="lm-button" :disabled="disableSubmit">
                <template v-if="!loading">
                  Guardar
                </template>
                <template v-else>
                  <i class="mdi mdi-loading lm-button-loading"></i>
                </template>
              </button>
              <img :src="logoLumileWhite" alt="Logo" width="80" class="mt-5">
            </div>
          </form>
        </b-col>
        <b-col md="8" class="d-none d-md-block">
          <section
            class="login-background"
          >
            <h1 class="login-title">¡Comenzá la transformación digital de tu empresa!</h1>
          </section>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import LmTextInput from '@/components/form/elements/LmTextInput'
import { mapState, mapGetters, mapActions } from 'vuex'
import { required, sameAs } from 'vuelidate/lib/validators'
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEsEsPackage from '@zxcvbn-ts/language-es-es'

export default {
  name: 'app',
  components: {
    LmTextInput
  },
  mixins: [lmToasts],
  data () {
    return {
      showInputValue: false,
      form: {
        newPassword: null,
        newPasswordAgain: null
      },
      minPasswordScore: 4,
      passwordStrength: null,
      serverErrorMessage: '',
      loading: false,
      restoredPasswordSuccessfully: false
    }
  },
  validations: {
    form: {
      newPassword: {
        required
      },
      newPasswordAgain: {
        sameAsNewPassword: sameAs('newPassword')
      }
    }
  },
  methods: {
    ...mapActions('notifications', ['getNotifications']),
    async saveNewPassword () {
      this.$v.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true
        try {
          await commonsApi.axiosPutWrapper(
            process.env?.VUE_APP_RESTORE_PASSWORD_URL + this.$route.query.token,
            {
              new_password: this.form.newPassword,
              new_password_again: this.form.newPasswordAgain
            }
          )
          this.restoredPasswordSuccessfully = true
          this.lmToastSuccessMessage('Se guardó la nueva contraseña.', 'Guardado con éxito')
          setTimeout(() => {
            this.$router.push('/login')
          }, 3000)
        } catch (err) {
          this.lmToastDangerMessage(err.response.data.message, `${err.response.data.type} - ${err.response.data.code}`)
        } finally {
          this.loading = false
        }
      }
    },
    evaluatePassword (el) {
      this.passwordStrength = zxcvbn(this.form.newPassword)
    }
  },
  computed: {
    ...mapGetters('session', ['isAuthenticated']),
    ...mapState('settings', ['appName', 'logoHomeImg', 'title1', 'title2', 'text', 'bgImage', 'loginBgColor', 'loginLogoWidth', 'loginLogoJustifyContent', 'loginTextAlign', 'logoLumileWhite']),
    authMode () {
      return process.env.VUE_APP_AUTH_MODE
    },
    disableSubmit () {
      return this.loading || this.passwordStrength?.score < this.minPasswordScore || this.$v.form.$invalid
    },
    passwordScoreText () {
      let scoreText
      if (this.passwordStrength?.score >= this.minPasswordScore) {
        scoreText = 'Fuerte'
      } else if (this.passwordStrength?.score === 0) {
        scoreText = 'Débil'
      } else {
        scoreText = 'Media'
      }
      return scoreText
    }

  },
  created () {
    if (this.isAuthenticated) {
      this.$router.replace('/')
    }
  },
  mounted () {
    this.$bvToast.hide('lm-toast-success-internal-link')
    const options = {
      translations: zxcvbnEsEsPackage.translations,
      graphs: zxcvbnCommonPackage.adjacencyGraphs,
      dictionary: {
        ...zxcvbnCommonPackage.dictionary,
        ...zxcvbnEsEsPackage.dictionary
      }
    }

    zxcvbnOptions.setOptions(options)
  }
}
</script>

<style lang="sass" scoped>
.login-wrapper
  padding: 0
  height: 100vh

@keyframes spin
  from
    transform: rotate(0deg)
  to
    transform: rotate(360deg)

.login-logo
  width: 90%

.login-form
  color: white
  padding: 0 20%
  height: 100%
  background: rgb(255,21,99)
  background: linear-gradient(90deg, rgba(255,21,99,1) 0%, rgba(176,58,135,1) 100%)

  .lm-button
    text-transform: uppercase
    background-color: white
    color: black
    border-radius: 20px
    border: none
    padding: 8px 20px
    margin-top: 40px
    font-weight: bold
    transition: all .4s ease-in-out
    &:disabled
      cursor: not-allowed
      background-color: rgba(232, 232, 232, .9)
    &:hover
      transform: scale(1.02)
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15)
    .lm-button-loading::before
      font-weight: bold
      animation-name: spin
      animation-duration: 1000ms
      animation-iteration-count: infinite
      animation-timing-function: linear
  .lm-link
    display: inline
    background: transparent
    color: white
    border: none
    border-bottom: 1px solid rgba(255, 255, 255, .4)
  .lm-input-label
    font-weight: bold
    display: flex
    flex-direction: column
  .lm-input
    font-weight: bold
    margin-top: 8px
    height: 42px
    border: none
    border-radius: 20px
    padding: 0 20px
    color: black
    background-color: rgba(241, 241, 241, .5)
    transition: all .4s ease-in-out
    &:focus
      outline: none
      box-shadow: 0 0 0 0.25rem rgba(58, 66, 165, .4)
    &::placeholder
      color: #4F4F4F
    &.error
      box-shadow: 0 0 0 0.25rem rgba(197, 0, 0, .4)
  .eyes
    position: absolute
    top: 23px
    right: 13px
    z-index: 1
  .lm-input-caption
    letter-spacing: .075rem
    font-size: .7rem
    padding: 6px 20px
    color: white

.login-background
  height: 100%
  background-color: white
  background-repeat: no-repeat
  background-attachment: fixed
  background-position: center
  background-size: 80%
  position: relative

.login-title
  width: 50%
  color: #3B43A6
  font-size: 2.6rem
  position: absolute
  top: 15%
  left: 5%

#wrapper-login
  background-position: center
  background-size: cover
  .col-left
    padding: 48px 55px
    max-width: 480px
    width: 100%
    @media(max-width: $xl)
      max-width: none
      padding: 72px 56px
    .logoSite
      @media(max-width: $xl)
        margin: 0 auto
        display: block
    h4
      font-size: 18px
      font-weight: 700
      color: $primaryFontThemeColor
    p
      color: $mutedFontThemeColor!important
      font-size: 14px
      font-weight: 400
    .buttonLogin
      box-shadow: 0 2px 6px 0 rgba(114,124,245,.5)
      width: 100%
      color: #fff
      font-size: 14px
      font-weight: 400
  .col-right
    padding: 6rem 3rem
    -webkit-box-flex: 1
    -ms-flex: 1
    flex: 1
    position: relative
    color: #fff
    text-align: center
    .auth-user-testimonial
      position: absolute
      margin: 0 auto
      padding: 0 1.75rem
      bottom: 3rem
      left: 0
      right: 0
      h2
        font-weight: 700
      p
        font-size: 14px
        &.lead
          font-weight: 400
          font-size: 18px
  .footer
    bottom: 0
    padding: 19px 30px 20px
    position: absolute
    right: 0
    color: $mutedFontThemeColor
    width: 100%
    text-align: center

</style>
